import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type { LoginEvent, SessionInfo } from '@platform/shared';
import { cx, Modal, Spinner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useLoad } from '../../stores/account';
import { loadMe, useMe } from '../../stores/session';
import { AIcon, Alert, Card, Chip, CodeBoxes, CopyButton, errMsg, Field, H2, mmss, Notice, PillButton, QrCode, Switch, useCountdown, when } from './kit';

const RULES: [string, (p: string) => boolean][] = [
  ['8+ characters', (p) => p.length >= 8],
  ['Uppercase letter', (p) => /[A-Z]/.test(p)],
  ['Lowercase letter', (p) => /[a-z]/.test(p)],
  ['Number', (p) => /\d/.test(p)],
];

function strength(p: string) {
  const passed = RULES.filter(([, f]) => f(p)).length;
  const bonus = p.length >= 12 && /[^A-Za-z0-9]/.test(p) ? 1 : 0;
  const lvl = Math.min(4, Math.max(passed + bonus - 1, 0));
  return { lvl, label: ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][lvl]!, color: ['bg-down', 'bg-down', 'bg-warn', 'bg-up', 'bg-up'][lvl]! };
}

function PasswordCard() {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const st = strength(next);
  const rulesOk = RULES.every(([, f]) => f(next));
  const match = confirm.length > 0 && confirm === next;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ loggedOutSessions: number }>('/api/security/password', { currentPassword: cur, newPassword: next });
      setCur('');
      setNext('');
      setConfirm('');
      setMsg({ ok: true, text: r.loggedOutSessions ? `Password changed. ${r.loggedOutSessions} other session${r.loggedOutSessions > 1 ? 's were' : ' was'} logged out.` : 'Password changed.' });
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <form onSubmit={submit} className="flex flex-col gap-7">
        <H2>Change password</H2>
        <Field label="Current password" htmlFor="sec-cur">
          <input id="sec-cur" type="password" required autoComplete="current-password" className="field" value={cur} onChange={(e) => setCur(e.target.value)} />
        </Field>
        <Field label="New password" htmlFor="sec-new">
          <input id="sec-new" type="password" required autoComplete="new-password" className="field" value={next} onChange={(e) => setNext(e.target.value)} />
          <div className="flex gap-1.5" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={cx('h-1.5 flex-1 rounded-full', next && (i < st.lvl || (st.lvl === 0 && i === 0)) ? st.color : 'bg-track')} />
            ))}
          </div>
          <div className="flex justify-between text-[14px]">
            <span className={cx('font-semibold', st.color.replace('bg-', 'text-'))}>{next ? st.label : ''}</span>
            <span className="text-muted">Strength</span>
          </div>
          <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0 text-[14px]">
            {RULES.map(([l, f]) => (
              <li key={l} className={cx('flex items-center gap-2', f(next) ? 'text-up' : 'text-muted')}>
                <AIcon name={f(next) ? 'check' : 'x'} size={14} />
                {l}
              </li>
            ))}
          </ul>
        </Field>
        <Field label="Confirm new password" htmlFor="sec-confirm" error={confirm && !match ? 'Passwords do not match' : null}>
          <input id="sec-confirm" type="password" required autoComplete="new-password" className="field" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Alert tone="info">Saving logs you out on every other device and sends a confirmation email.</Alert>
        <Notice msg={msg} onClose={() => setMsg(null)} />
        <PillButton type="submit" busy={busy} disabled={!cur || !rulesOk || !match}>
          Update password
        </PillButton>
      </form>
    </Card>
  );
}

type Challenge = { challengeId: string; devCode?: string; resendIn: number };
type AppInfo = { enabled: boolean; backupCodesLeft: number; emailCodeEnabled: boolean };

const DevCode = ({ code }: { code?: string }) =>
  code ? (
    <p className="m-0 rounded-2xl bg-accent-bg px-4 py-3 text-[13px] text-accent-fg">
      Dev mode (no SMTP configured): your code is <span className="num font-semibold">{code}</span>
    </p>
  ) : null;

const Done = ({ title, children, onDone }: { title: string; children: ReactNode; onDone: () => void }) => (
  <>
    <span className="mx-auto grid size-14 place-items-center rounded-full bg-up-bg text-up">
      <AIcon name="check" size={26} />
    </span>
    <p className="m-0 text-center text-[20px] font-bold">{title}</p>
    <p className="m-0 text-center text-[14px] text-muted">{children}</p>
    <PillButton onClick={onDone} className="w-full">
      Done
    </PillButton>
  </>
);

/**
 * Turning the email code at login on or off. The switch has already sent the first code; the
 * setting changes only once that code (and, to turn off, the password) is accepted.
 */
function EmailCodeModal({ turnOn, first, email, onClose, onChanged }: { turnOn: boolean; first: Challenge; email: string; onClose: () => void; onChanged: () => Promise<void> }) {
  const [ch, setCh] = useState(first);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [wait, startWait] = useCountdown();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; dead: boolean } | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => startWait(first.resendIn), [first, startWait]);

  const resend = async () => {
    setErr(null);
    setCode('');
    try {
      const r = await api.post<Challenge>('/api/security/email-code/send', { turnOn });
      setCh(r);
      startWait(r.resendIn);
    } catch (x) {
      setErr({ text: errMsg(x), dead: false });
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.post(turnOn ? '/api/security/email-code/enable' : '/api/security/email-code/disable', { challengeId: ch.challengeId, code, ...(turnOn ? {} : { password }) });
      await onChanged();
      if (turnOn) setDone(true);
      else onClose();
    } catch (x) {
      const c = x instanceof ApiError ? x.code : '';
      const dead = c === 'code_expired' || c === 'code_locked';
      setErr({
        text: dead ? 'This code has expired or was tried too many times. Send a new one to try again.' : c === 'code_invalid' ? "That code doesn't match. Check the email and try again." : errMsg(x),
        dead,
      });
      if (c !== 'wrong_password') setCode('');
    } finally {
      setBusy(false);
    }
  };

  const title = turnOn ? 'Turn on email code' : 'Turn off email code?';
  return (
    <Modal open onClose={onClose} title={title} width={420} locked={done}>
      {done ? (
        <Done title="Email code is on" onDone={onClose}>
          From your next login, we'll ask for a code sent to <b className="text-fg">{email}</b> after your password. We've emailed you a confirmation.
        </Done>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          {turnOn ? (
            <p className="m-0 text-[15px] text-muted">
              We sent a 6-digit code to <b className="text-fg">{email}</b>. Enter it to confirm this inbox gets our emails.
            </p>
          ) : (
            <Alert>Without it, anyone who knows your password can log in. We'll email you when it's off.</Alert>
          )}
          <DevCode code={ch.devCode} />
          {!turnOn && (
            <Field label="Password" htmlFor="ec-pw">
              <input id="ec-pw" type="password" required autoComplete="current-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
          )}
          {err?.dead ? (
            <Alert>{err.text}</Alert>
          ) : (
            <Field label={turnOn ? 'Verification code' : `Code sent to ${email}`} htmlFor="ec-code">
              <CodeBoxes
                id="ec-code"
                label="Verification code"
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setErr(null);
                }}
                error={!!err}
                autoFocus={turnOn}
              />
            </Field>
          )}
          {err && !err.dead && (
            <p className="m-0 text-[14px] text-down" role="alert">
              {err.text}
            </p>
          )}
          <div className="flex justify-between gap-3 text-[14px]">
            {wait > 0 ? (
              <span className="text-muted">
                Resend code in <span className="num">{mmss(wait)}</span>
              </span>
            ) : (
              <button type="button" onClick={() => void resend()} className="font-semibold text-accent-fg">
                {err?.dead ? 'Send a new code' : 'Resend code'}
              </button>
            )}
            <span className="text-muted">Not there? Check your spam folder</span>
          </div>
          {turnOn ? (
            <PillButton type="submit" busy={busy} disabled={code.length !== 6 || !!err?.dead} className="w-full">
              Verify and turn on
            </PillButton>
          ) : (
            <div className="flex gap-3">
              <PillButton kind="secondary" onClick={onClose} className="flex-1">
                Keep it on
              </PillButton>
              <PillButton type="submit" kind="destructive" busy={busy} disabled={code.length !== 6 || !password || !!err?.dead} className="flex-1">
                Turn off
              </PillButton>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}

function downloadCodes(codes: string[]) {
  const url = URL.createObjectURL(new Blob([`Backup codes (each works once)\n\n${codes.join('\n')}\n`], { type: 'text/plain' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: 'backup-codes.txt' });
  a.click();
  URL.revokeObjectURL(url);
}

/** The new backup codes, shown once: Done stays off until the person says they saved them. */
function BackupCodesStep({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <>
      <p className="m-0 text-[15px] text-muted">If you lose your phone, each code lets you log in once. We show them only now.</p>
      <div className="num grid grid-cols-2 gap-x-5 gap-y-2 rounded-[16px] bg-track px-4 py-3.5 text-[15px]">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="flex gap-3">
        <CopyButton value={codes.join('\n')} kind="secondary" size="sm" className="flex-1">
          Copy all
        </CopyButton>
        <PillButton kind="secondary" size="sm" onClick={() => downloadCodes(codes)} className="flex-1">
          Download .txt
        </PillButton>
      </div>
      <label className="flex cursor-pointer items-center gap-2.5 text-[14px]">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="size-[18px] accent-fg" />
        I've saved these codes somewhere safe
      </label>
      <PillButton onClick={onDone} disabled={!saved} className="w-full">
        Done
      </PillButton>
    </>
  );
}

const Steps = ({ at }: { at: 1 | 2 | 3 }) => (
  <div className="flex gap-1.5" aria-label={`Step ${at} of 3`}>
    {[1, 2, 3].map((i) => (
      <span key={i} className={cx('h-1 flex-1 rounded-full', i <= at ? 'bg-fg' : 'bg-line')} />
    ))}
  </div>
);

/** Authenticator app setup: scan, prove the app shows the same codes, save the backup codes. */
function AppSetupModal({ setup, onClose, onEnabled }: { setup: { secret: string; otpauthUrl: string }; onClose: () => void; onEnabled: () => Promise<void> }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ backupCodes: string[] }>('/api/security/totp/enable', { code });
      setCodes(r.backupCodes);
      setStep(3);
      await onEnabled();
    } catch (x) {
      setErr(errMsg(x));
      setCode('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={step === 3 ? 'Save your backup codes' : 'Set up authenticator app'} width={420} locked={step === 3}>
      <Steps at={step} />
      {step === 1 && (
        <>
          <p className="m-0 text-[15px] text-muted">Open Google Authenticator, Authy or any TOTP app and scan this code.</p>
          <QrCode value={setup.otpauthUrl} size={164} label="QR code to scan with your authenticator app" className="mx-auto" />
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] text-muted">Can't scan? Enter this key in the app:</span>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-track px-3 py-2">
              <code className="num text-[14px] break-all">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              <CopyButton value={setup.secret} kind="secondary" size="sm">
                Copy
              </CopyButton>
            </div>
          </div>
          <PillButton onClick={() => setStep(2)} className="w-full">
            Next
          </PillButton>
        </>
      )}
      {step === 2 && (
        <form onSubmit={verify} className="flex flex-col gap-4">
          <p className="m-0 text-[15px] text-muted">Enter the 6-digit code your app shows for this account. It changes every 30 seconds.</p>
          <CodeBoxes
            id="app-code"
            label="Authenticator code"
            value={code}
            onChange={(v) => {
              setCode(v);
              setErr(null);
            }}
            error={!!err}
            autoFocus
          />
          {err && (
            <p className="m-0 text-[14px] text-down" role="alert">
              {err}
            </p>
          )}
          <div className="flex gap-3">
            <PillButton kind="secondary" onClick={() => setStep(1)} className="flex-1">
              Back
            </PillButton>
            <PillButton type="submit" busy={busy} disabled={code.length !== 6} className="flex-1">
              Verify
            </PillButton>
          </div>
        </form>
      )}
      {step === 3 && <BackupCodesStep codes={codes} onDone={onClose} />}
    </Modal>
  );
}

/** Turn off the authenticator app, or replace the backup codes: both need a current code. */
function AppCodeModal({ mode, onClose, onChanged }: { mode: 'disable' | 'regen'; onClose: () => void; onChanged: () => Promise<void> }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'disable') {
        await api.post('/api/security/totp/disable', { password, code });
        await onChanged();
        onClose();
      } else {
        setCodes((await api.post<{ backupCodes: string[] }>('/api/security/totp/backup-codes', { code })).backupCodes);
        await onChanged();
      }
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  if (codes) {
    return (
      <Modal open onClose={onClose} title="Save your backup codes" width={420} locked>
        <BackupCodesStep codes={codes} onDone={onClose} />
      </Modal>
    );
  }
  return (
    <Modal open onClose={onClose} title={mode === 'disable' ? 'Turn off authenticator app?' : 'Replace your backup codes'} width={420}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {mode === 'disable' ? (
          <Alert>Your login goes back to the email code if that's on, otherwise to your password only.</Alert>
        ) : (
          <p className="m-0 text-[15px] text-muted">Your old backup codes stop working as soon as the new ones are made.</p>
        )}
        {mode === 'disable' && (
          <Field label="Password" htmlFor="app-off-pw">
            <input id="app-off-pw" type="password" required autoComplete="current-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )}
        <Field label="Authenticator or backup code" htmlFor="app-off-code">
          <input id="app-off-code" required autoComplete="one-time-code" className="field num" value={code} onChange={(e) => setCode(e.target.value.trim())} />
        </Field>
        {err && (
          <p className="m-0 text-[14px] text-down" role="alert">
            {err}
          </p>
        )}
        <div className="flex gap-3">
          <PillButton kind="secondary" onClick={onClose} className="flex-1">
            Cancel
          </PillButton>
          <PillButton type="submit" busy={busy} disabled={!code || (mode === 'disable' && !password)} kind={mode === 'disable' ? 'destructive' : 'primary'} className="flex-1">
            {mode === 'disable' ? 'Turn off' : 'Generate codes'}
          </PillButton>
        </div>
      </form>
    </Modal>
  );
}

type Open =
  | { kind: 'email'; turnOn: boolean; challenge: Challenge }
  | { kind: 'app-setup'; setup: { secret: string; otpauthUrl: string } }
  | { kind: 'app-off' }
  | { kind: 'app-regen' };

/** `staff`: My sign-in security in Platform Admin, without the client's email-code row. */
export function TwoFactorCard({ staff = false, onChange }: { staff?: boolean; onChange?: () => void } = {}) {
  const me = useMe()!;
  const { data: info, reload } = useLoad<AppInfo>('/api/security/totp', [me.totpEnabled, me.emailCodeEnabled]);
  const [open, setOpen] = useState<Open | null>(null);
  const [busy, setBusy] = useState<'email' | 'app' | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const enabled = info?.enabled ?? me.totpEnabled;
  const emailOn = info?.emailCodeEnabled ?? me.emailCodeEnabled;

  const changed = async () => {
    await loadMe();
    await reload();
    onChange?.();
  };
  const start = async (which: 'email' | 'app', fn: () => Promise<Open>) => {
    setBusy(which);
    setMsg(null);
    try {
      setOpen(await fn());
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(null);
    }
  };
  // The switch sends the first code itself, so the pop-up opens with it already on its way.
  const flipEmail = (turnOn: boolean) =>
    start('email', async () => ({ kind: 'email', turnOn, challenge: await api.post<Challenge>('/api/security/email-code/send', { turnOn }) }));
  const setUpApp = () => start('app', async () => ({ kind: 'app-setup', setup: await api.post('/api/security/totp/setup') }));

  const emailSub = enabled
    ? emailOn
      ? 'Standby · your authenticator app is used instead'
      : 'Off · your authenticator app protects your login'
    : emailOn
      ? `On · ${me.email}`
      : `Off · we'll email a code to ${me.email}`;

  return (
    <Card className="flex flex-col gap-6">
      <H2>{staff ? 'Authenticator app' : 'Two-factor authentication'}</H2>
      <div className="rounded-[20px] border border-line px-5">
        {!staff && (
          <div className="flex items-center gap-4 border-b border-line py-5">
            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-track">
              <AIcon name="mail" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[17px]">Email code at login</div>
              <div className="text-[15px] break-words text-muted">{emailSub}</div>
            </div>
            <Switch label="Email code at login" checked={emailOn} busy={busy === 'email'} locked={enabled} onChange={(next) => void flipEmail(next)} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 py-5">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-track">
            <AIcon name="phone" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[17px]">Authenticator app</div>
            <div className="text-[15px] text-muted">{enabled ? `On · ${info?.backupCodesLeft ?? '—'} backup codes left` : 'Google Authenticator or any TOTP app'}</div>
          </div>
          {enabled ? (
            <Chip tone="up">On</Chip>
          ) : (
            <PillButton size="sm" onClick={() => void setUpApp()} busy={busy === 'app'}>
              Set up
            </PillButton>
          )}
        </div>
      </div>

      {enabled && (
        <div className="flex flex-wrap gap-3">
          <PillButton kind="secondary" size="sm" onClick={() => setOpen({ kind: 'app-regen' })}>
            New backup codes
          </PillButton>
          <PillButton kind="danger" size="sm" onClick={() => setOpen({ kind: 'app-off' })}>
            Turn off
          </PillButton>
        </div>
      )}
      <Notice msg={msg} onClose={() => setMsg(null)} />

      {open?.kind === 'email' && (
        <EmailCodeModal
          turnOn={open.turnOn}
          first={open.challenge}
          email={me.email}
          onClose={() => setOpen(null)}
          onChanged={async () => {
            await changed();
            if (!open.turnOn) setMsg({ ok: true, text: 'Email code at login is off.' });
          }}
        />
      )}
      {open?.kind === 'app-setup' && <AppSetupModal setup={open.setup} onClose={() => setOpen(null)} onEnabled={changed} />}
      {(open?.kind === 'app-off' || open?.kind === 'app-regen') && <AppCodeModal mode={open.kind === 'app-off' ? 'disable' : 'regen'} onClose={() => setOpen(null)} onChanged={changed} />}
    </Card>
  );
}

function Sessions() {
  const { data, error, reload } = useLoad<SessionInfo[]>('/api/security/sessions');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      await reload();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(null);
    }
  };
  const others = data?.filter((s) => !s.current).length ?? 0;
  // The API sends IPs to staff accounts only.
  const showIp = !!data?.some((s) => s.ip !== undefined);
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Active sessions</h2>
        <PillButton kind="danger" size="sm" disabled={!others} busy={busy === 'all'} onClick={() => void act('all', () => api.post('/api/security/sessions/logout-others'))}>
          Log out all other sessions
        </PillButton>
      </div>
      <Notice msg={error ? { ok: false, text: error } : msg} />
      {!data ? (
        <Spinner />
      ) : (
        <table className="w-full border-collapse text-[15px] md:text-[17px]">
          <thead>
            <tr className="border-b border-line">
              <th className="px-2 py-4 text-left font-semibold md:px-6">Device</th>
              {showIp && <th className="px-3 py-4 text-left font-semibold max-md:hidden">IP address</th>}
              <th className="px-3 py-4 text-left font-semibold max-md:hidden">Signed in</th>
              <th className="px-3 py-4 text-left font-semibold">Last active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((s) => (
              <tr key={s.id} className="border-b border-line">
                <td className="px-2 py-4 md:px-6 md:py-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-track">
                      <AIcon name={/iPhone|iPad|Android/.test(s.device) ? 'phone' : 'grid'} size={18} />
                    </span>
                    <span>{s.device}</span>
                    {s.current && <Chip tone="accent">This device</Chip>}
                  </div>
                  {showIp && <div className="num mt-1 text-[13px] text-muted md:hidden">{s.ip}</div>}
                </td>
                {showIp && <td className="num px-3 text-[15px] text-muted max-md:hidden">{s.ip ?? '—'}</td>}
                <td className="num px-3 text-[15px] text-muted max-md:hidden">{when(s.signedInAt)}</td>
                <td className={cx('num px-3 text-[15px]', s.current && 'text-up')}>{s.current ? 'Now' : when(s.lastActiveAt)}</td>
                <td className="px-2 text-right md:px-6">
                  {!s.current && (
                    <PillButton kind="secondary" size="sm" busy={busy === s.id} onClick={() => void act(s.id, () => api.del(`/api/security/sessions/${s.id}`))}>
                      Log out
                    </PillButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const REASON: Record<string, string> = {
  wrong_password: 'Wrong password',
  wrong_code: 'Wrong code',
  disabled: 'Account disabled',
  temp_password_expired: 'Temporary password expired',
  temp_password_set_by_staff: 'Temporary password set by support',
  password_set_by_staff: 'Password set by support',
  reset_link_sent_by_staff: 'Reset link sent by support',
  welcome_link_sent_by_staff: 'Welcome email sent by support',
};
const METHOD: Record<string, string> = { email: 'Email code', totp: 'Authenticator', backup: 'Backup code', password: 'Password', none: 'Password' };

function LoginHistory() {
  const { data, error } = useLoad<LoginEvent[]>('/api/security/logins');
  const showIp = !!data?.some((e) => e.ip !== undefined);
  return (
    <section className="flex flex-col gap-4">
      <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">
        Login history <span className="text-[15px] font-normal text-muted">last 20</span>
      </h2>
      <Notice msg={error ? { ok: false, text: error } : null} />
      {!data ? (
        <Spinner />
      ) : !data.length ? (
        <p className="text-muted">No logins recorded yet.</p>
      ) : (
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full border-collapse text-[15px] md:text-[17px]">
            <thead className="sticky top-0 bg-bg">
              <tr className="border-b border-line">
                <th className="px-2 py-4 text-left font-semibold md:px-6">Date</th>
                {showIp && <th className="px-3 py-4 text-left font-semibold max-md:hidden">IP</th>}
                <th className="px-3 py-4 text-left font-semibold max-md:hidden">Device</th>
                <th className="px-3 py-4 text-left font-semibold max-lg:hidden">Method</th>
                <th className="px-2 py-4 text-right font-semibold md:px-6">Result</th>
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} className="border-b border-line">
                  <td className="px-2 py-4 md:px-6">
                    <span className="num text-[15px]">{when(e.at, true)}</span>
                    <div className="text-[13px] text-muted md:hidden">
                      {e.device}
                      {showIp && (
                        <>
                          {' · '}
                          <span className="num">{e.ip}</span>
                        </>
                      )}
                    </div>
                  </td>
                  {showIp && <td className="num px-3 text-[15px] text-muted max-md:hidden">{e.ip ?? '—'}</td>}
                  <td className="px-3 max-md:hidden">{e.device}</td>
                  <td className="px-3 text-muted max-lg:hidden">{METHOD[e.method ?? ''] ?? '—'}</td>
                  <td className="px-2 text-right md:px-6">{e.success ? <Chip tone="up">Success</Chip> : <Chip tone="down">{REASON[e.reason ?? ''] ?? 'Failed'}</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function SecurityTab() {
  // Active sessions and login history are for staff accounts only; clients see password and 2FA.
  const staff = useMe()?.role === 'admin';
  return (
    <div className="flex flex-col gap-8 xl:gap-11">
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-2">
        <PasswordCard />
        <TwoFactorCard />
      </div>
      {staff && <Sessions />}
      {staff && <LoginHistory />}
    </div>
  );
}
